import type { NavigationItem } from "skedyul-core/features/workplaces/features/navigationItems/resources/types";

export type SidebarNavigationEditorItem = {
  id: string;
  url: string;
  label: string;
  icon?: string | null;
  sortIndex: number;
  parentId?: string | null;
  pagePath?: string | null;
  pageId?: string | null;
  pageIsPrivate?: boolean;
  navScope?: "personal" | "team";
  kind: "group" | "section" | "link";
  children?: SidebarNavigationEditorItem[];
};

export type NavigationLayoutChange = {
  id: string;
  parentId: string | null;
  sortIndex: number;
};

function mapNavigationItemsToEditorTree(
  items: NavigationItem[],
): SidebarNavigationEditorItem[] {
  return items.map((item) => {
    const kind: SidebarNavigationEditorItem["kind"] = item.url.startsWith(
      "_section/",
    )
      ? "section"
      : item.url.startsWith("_group/")
        ? "group"
        : "link";

    return {
      id: item.id,
      url: item.url,
      label: item.label || item.page?.path || item.url,
      icon: item.icon,
      sortIndex: item.sortIndex,
      parentId: item.parentId ?? null,
      pagePath: item.page?.path ?? null,
      pageId: item.page?.id ?? null,
      pageIsPrivate:
        item.page?.memberId !== undefined && item.page.memberId !== null,
      kind,
      children: item.children
        ? mapNavigationItemsToEditorTree(item.children)
        : undefined,
    };
  });
}

export function findEditorItemById(
  items: SidebarNavigationEditorItem[],
  id: string,
): SidebarNavigationEditorItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children?.length) {
      const found = findEditorItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

export function findEditorItemByPageId(
  items: SidebarNavigationEditorItem[],
  pageId: string,
): SidebarNavigationEditorItem | null {
  for (const item of items) {
    if (item.pageId === pageId) return item;
    if (item.children?.length) {
      const found = findEditorItemByPageId(item.children, pageId);
      if (found) return found;
    }
  }
  return null;
}

function isEditorItemDescendant(
  items: SidebarNavigationEditorItem[],
  ancestorId: string,
  candidateId: string,
): boolean {
  const ancestor = findEditorItemById(items, ancestorId);
  if (!ancestor?.children?.length) return false;

  const walk = (nodes: SidebarNavigationEditorItem[]): boolean => {
    for (const node of nodes) {
      if (node.id === candidateId) return true;
      if (node.children?.length && walk(node.children)) return true;
    }
    return false;
  };

  return walk(ancestor.children);
}

export function isDropTargetInPersonalScope(
  items: SidebarNavigationEditorItem[],
  targetParentId: string | null,
): boolean {
  const personalRoot = items.find(
    (item) => item.navScope === "personal" && item.kind === "section",
  );
  if (!personalRoot) {
    return false;
  }

  if (targetParentId === personalRoot.id) {
    return true;
  }

  if (!targetParentId) {
    return false;
  }

  return isEditorItemDescendant(items, personalRoot.id, targetParentId);
}

function applyNavScope(
  items: SidebarNavigationEditorItem[],
  navScope: "personal" | "team",
): SidebarNavigationEditorItem[] {
  return items.map((item) => ({
    ...item,
    navScope,
    children: item.children
      ? applyNavScope(item.children, navScope)
      : undefined,
  }));
}

export function tagNavigationEditorTree(
  items: NavigationItem[],
  navScope: "personal" | "team",
): SidebarNavigationEditorItem[] {
  return applyNavScope(mapNavigationItemsToEditorTree(items), navScope);
}

export function buildCombinedSidebarEditorTree(
  myWorkspaceNavigationItems: NavigationItem[],
  teamNavigationItems: NavigationItem[],
): SidebarNavigationEditorItem[] {
  return [
    ...tagNavigationEditorTree(myWorkspaceNavigationItems, "personal"),
    ...tagNavigationEditorTree(teamNavigationItems, "team"),
  ];
}

export function splitLayoutChangesByScope(
  changes: NavigationLayoutChange[],
  navTree: SidebarNavigationEditorItem[],
): {
  personal: NavigationLayoutChange[];
  team: NavigationLayoutChange[];
} {
  const personal: NavigationLayoutChange[] = [];
  const team: NavigationLayoutChange[] = [];

  for (const change of changes) {
    const item = findEditorItemById(navTree, change.id);
    if (item?.navScope === "team") {
      team.push(change);
    } else {
      personal.push(change);
    }
  }

  return { personal, team };
}

export function getNavigationItemScope(
  navTree: SidebarNavigationEditorItem[],
  id: string,
): "personal" | "team" | null {
  return findEditorItemById(navTree, id)?.navScope ?? null;
}

export type CrossBoundaryMoveIntent =
  | {
      type: "promote_private_to_team";
      memberNavItemId: string;
      targetParentId: string;
      sortIndex: number;
    }
  | {
      type: "move_personal_to_team";
      memberNavItemId: string;
      targetParentId: string;
      sortIndex: number;
    }
  | { type: "pin_team_to_personal"; teamNavItemId: string; sortIndex: number }
  | null;

export function detectCrossBoundaryMoveIntent(
  navTree: SidebarNavigationEditorItem[],
  dragId: string,
  targetParentId: string | null,
  targetIndex: number,
): CrossBoundaryMoveIntent {
  const dragged = findEditorItemById(navTree, dragId);
  if (!dragged || !targetParentId) {
    return null;
  }

  const droppingIntoPersonal = isDropTargetInPersonalScope(
    navTree,
    targetParentId,
  );
  const droppingIntoTeam = !droppingIntoPersonal;

  // Any personal → team drop must not go through same-scope reorder (team
  // section ids are not valid member-nav parents).
  if (
    dragged.navScope === "personal" &&
    droppingIntoTeam &&
    dragged.kind === "link" &&
    dragged.pageId
  ) {
    if (dragged.pageIsPrivate) {
      return {
        type: "promote_private_to_team",
        memberNavItemId: dragId,
        targetParentId,
        sortIndex: targetIndex,
      };
    }

    return {
      type: "move_personal_to_team",
      memberNavItemId: dragId,
      targetParentId,
      sortIndex: targetIndex,
    };
  }

  if (
    dragged.navScope === "team" &&
    dragged.kind === "link" &&
    droppingIntoPersonal
  ) {
    return {
      type: "pin_team_to_personal",
      teamNavItemId: dragId,
      sortIndex: targetIndex,
    };
  }

  return null;
}

export function collectNavigationPagesFromTrees(
  trees: NavigationItem[][],
): { pageIds: Set<string>; pagePaths: Set<string> } {
  const pageIds = new Set<string>();
  const pagePaths = new Set<string>();

  const walk = (nodes: NavigationItem[]) => {
    for (const item of nodes) {
      if (item.page?.id) {
        pageIds.add(item.page.id);
      }
      if (item.page?.path) {
        pagePaths.add(item.page.path);
      } else if (
        !item.url.startsWith("_section/") &&
        !item.url.startsWith("_group/")
      ) {
        pagePaths.add(item.url);
      }

      if (item.children?.length) {
        walk(item.children);
      }
    }
  };

  for (const tree of trees) {
    walk(tree);
  }

  return { pageIds, pagePaths };
}
